/**
 * Seeding the table creator from a CSV (DESIGN_EDITING.md console flow 3): the table name from the file name, and one
 * column per header with its type guessed from the preview, with an automatic id key added first unless the file
 * already has an `id`. Pure: it joins the three pure modules (csv, typeGuess, tableName) and nothing else.
 */
import { CsvShape, columnValues } from './csv';
import { guessType } from './typeGuess';
import { DraftColumn, toSnakeCase } from './tableName';

/** The table name from a file name: drop the last extension, then lower snake case. Empty becomes "imported". */
export const tableNameFromFile = (fileName: string): string => toSnakeCase(fileName.replace(/\.[^.]*$/, '')) || 'imported';

/**
 * The draft columns for a new table from a CSV preview. Each header becomes a `fromCsv` column (editable type, not
 * removable). When no header is called `id`, an automatic id key is added first; otherwise the user picks the key.
 */
export const draftFromCsv = (shape: CsvShape): DraftColumn[] => {
    const cols: DraftColumn[] = shape.header.map((h, i) => ({
        name: toSnakeCase(h) || `column_${i + 1}`,
        type: guessType(columnValues(shape, i)),
        required: false,
        isKey: false,
        defaultKind: 'none',
        defaultValue: '',
        fromCsv: true,
    }));
    if (cols.some((c) => c.name === 'id')) return cols;
    return [{ name: 'id', type: 'bigint', required: true, isKey: true, defaultKind: 'identity', defaultValue: '', locked: true }, ...cols];
};

/** The import column list for the commit: the file's columns, in order, by their current (possibly edited) name. */
export const importColumnsOf = (columns: DraftColumn[]): string[] => columns.filter((c) => c.fromCsv).map((c) => c.name);
